import Controller from '@ember/controller';
import { inject as service } from '@ember/service';
import { action, computed } from '@ember/object';
import { task } from 'ember-concurrency';
import TransactionalMessageModel, {
  TransactionalMessageUpdateType,
  ContentSource,
} from 'cio/models/transactional-message';
import { TransactionalCodeSnippetLanguage } from 'cio/utils/transactional/code-snippet-request-renderer';
import ExtApiKeyModel from 'cio/models/ext-api-key';
import AnalyticsService from 'cio/services/analytics';
import ApiErrorsService from 'cio/services/api-errors';
import DS from 'ember-data';
import RouterService from '@ember/routing/router-service';

export default class TransactionalMessageSetupReviewController extends Controller {
  @service analytics!: AnalyticsService;
  @service apiErrors!: ApiErrorsService;
  @service store!: DS.Store;
  @service router!: RouterService;

  highlightedGroup?: number;
  hasFoundDeliveries = false;
  activeSnippetName: TransactionalCodeSnippetLanguage = 'Node.js';

  extApiKeys!: DS.PromiseArray<ExtApiKeyModel>;
  selectedExtApiKey!: ExtApiKeyModel;

  declare model: TransactionalMessageModel;

  async setDefaults() {
    this.setProperties({
      hasFoundDeliveries: false,
      activeSnippetName: 'Node.js',
    });

    await this.fetchExtApiKeysTask.perform();
    // eslint-disable-next-line ember/no-array-prototype-extensions
    if (this.extApiKeys.toArray().length >= 1) {
      this.set('selectedExtApiKey', this.extApiKeys.slice()[0]);
    }
  }

  get isDirty() {
    return this.hasFoundDeliveries;
  }

  // eslint-disable-next-line getter-return
  @computed('activeSnippetName')
  get setup() {
    switch (this.activeSnippetName) {
      case 'Node.js':
        return {
          installation: {
            highlightGroup: 0,
            title: 'Install the Customer.io library',
            subtitle: 'Install the customerio library and import it in your code.',
            command: 'npm install customerio-node --save',
          },
          createInstance: {
            highlightGroup: 1,
            title: 'Create an instance with your App API key',
            subtitle: null,
          },
          defineMessage: {
            highlightGroup: 2,
          },
          sendMessage: {
            highlightGroup: 3,
          },
        };
      case 'Python':
        return {
          installation: {
            highlightGroup: 0,
            title: 'Install the Customer.io package',
            subtitle: 'Install the customerio package and import it in your code.',
            command: 'pip install customerio',
          },
          createInstance: {
            highlightGroup: 1,
            title: 'Create an instance with your App API key',
            subtitle: null,
          },
          defineMessage: {
            highlightGroup: 2,
          },
          sendMessage: {
            highlightGroup: 3,
          },
        };
      case 'Go':
        return {
          installation: {
            highlightGroup: 0,
            title: 'Install the Customer.io package',
            subtitle: 'Install the go-customerio package and import it in your code.',
            command: 'go get "github.com/customerio/go-customerio"',
          },
          createInstance: {
            highlightGroup: 1,
            title: 'Create an instance with your App API key',
            subtitle: null,
          },
          defineMessage: {
            highlightGroup: 2,
          },
          sendMessage: {
            highlightGroup: 3,
          },
        };
      case 'Ruby':
        return {
          installation: {
            highlightGroup: 0,
            title: 'Install the Customer.io library',
            subtitle: 'Install the customerio gem and import it in your code.',
            command: 'gem install customerio',
          },
          createInstance: {
            highlightGroup: 1,
            title: 'Create an instance with your App API key',
            subtitle: null,
          },
          defineMessage: {
            highlightGroup: 2,
          },
          sendMessage: {
            highlightGroup: 3,
          },
        };
      case 'cURL':
        // cURL snippet is slightly different and requires this object in the
        // first place. For every other snippet, we have four distinct groups,
        // but for cURL, there's only two. We also show only three steps for it
        // ("create instance", "define message", and "send message") and first
        // step highlights nothing.
        return {
          installation: null,
          createInstance: {
            highlightGroup: null,
            title: 'Choose your API key',
            subtitle: "Here's the API key that you'll use to send the message.",
          },
          defineMessage: {
            highlightGroup: 0,
          },
          sendMessage: {
            highlightGroup: 1,
          },
        };
    }
  }

  updateSourceAndRedirectToComposerTask = task(async () => {
    try {
      this.model.setProperties({
        updateType: TransactionalMessageUpdateType.ContentSource,
        contentSource: ContentSource.Template,
      });

      await this.model.save();

      this.router.transitionTo('composer.transactional', this.model.id);
    } catch (err) {
      this.apiErrors.report(err);
    }
  });

  @action
  onDeliveriesFound() {
    this.set('hasFoundDeliveries', true);
  }

  saveTask = task(async () => {
    this.model.set('updateType', TransactionalMessageUpdateType.Activate);
    await this.model.save();

    this.analytics.track('transactional', 'activated', { model: this.model });
    this.router.transitionTo('transactional.message.overview', this.model.id);
  });

  @action
  didSelectKey(key: ExtApiKeyModel) {
    this.set('selectedExtApiKey', key);
  }

  @action
  highlightGroup(n: number | null) {
    this.set('highlightedGroup', n ?? 0);
  }

  @action
  onCopy(source: string) {
    this.analytics.track('transactional', 'code_copied', {
      source,
      language: this.activeSnippetName,
      model: this.model,
    });
  }

  fetchExtApiKeysTask = task(async () => {
    this.set('extApiKeys', await this.store.findAll('ext-api-key'));
  });
}

declare module '@ember/controller' {
  interface Registry {
    'transactional-message-setup-review': TransactionalMessageSetupReviewController;
  }
}
